/**
 * Unidades e conversões do laboratório.
 *
 * Toda grandeza do domínio leva a unidade no nome do campo (`raioM`,
 * `vazaoKgS`) ou num campo `unidade` ao lado do valor. As conversões ficam
 * aqui, num lugar só, para que "pés para metros" não apareça escrito à mão
 * em três oficinas com três arredondamentos diferentes.
 */

export type UnidadeConcentracao = 'ppm' | 'mg/m3'

/** 1 pé internacional, em metros (exato por definição). */
export const METROS_POR_PE = 0.3048
/** 1 milha terrestre internacional, em metros (exato por definição). */
export const METROS_POR_MILHA = 1609.344

/** Volume molar de gás ideal a 25 °C e 1 atm, L/mol — convenção usada pela EPA nos AEGL. */
export const VOLUME_MOLAR_25C_L = 24.45

/** Massa molar média do ar seco, g/mol. */
export const MASSA_MOLAR_AR = 28.96

export const pesParaMetros = (pes: number): number => pes * METROS_POR_PE
export const milhasParaMetros = (milhas: number): number => milhas * METROS_POR_MILHA

/** ppm (v/v) → mg/m³ a 25 °C e 1 atm. */
export const ppmParaMgM3 = (ppm: number, massaMolarGMol: number): number =>
  (ppm * massaMolarGMol) / VOLUME_MOLAR_25C_L

/** mg/m³ → ppm (v/v) a 25 °C e 1 atm. */
export const mgM3ParaPpm = (mgM3: number, massaMolarGMol: number): number =>
  (mgM3 * VOLUME_MOLAR_25C_L) / massaMolarGMol

/** kg/m³ de gás no ar → mg/m³. */
export const kgM3ParaMgM3 = (kgM3: number): number => kgM3 * 1e6

/** Concentração em mg/m³ de um limite expresso em qualquer unidade. */
export function emMgM3(valor: number, unidade: UnidadeConcentracao, massaMolarGMol: number): number {
  return unidade === 'mg/m3' ? valor : ppmParaMgM3(valor, massaMolarGMol)
}

/** Densidade do vapor relativa ao ar, pela razão das massas molares (gás ideal). */
export const densidadeRelativaAoAr = (massaMolarGMol: number): number => massaMolarGMol / MASSA_MOLAR_AR

/** Formata metros como "850 m" ou "1,6 km", no padrão pt-BR. */
export function formatarDistancia(metros: number): string {
  if (!Number.isFinite(metros)) return '—'
  if (metros < 1000) return `${Math.round(metros).toLocaleString('pt-BR')} m`
  return `${(metros / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: 1 })} km`
}
